import { useEffect } from 'react';
import { useLogger } from '../../services/logging/logger-context';
import { ROUTES } from '../../types/routes.enum';

/**
 * OAuth Callback Handler Component
 *
 * HOW THIS WORKS:
 * ----------------
 * 1. User initiates SSO sign-in via signInWithSso() in useAuth.tsx
 * 2. User is redirected to external SSO provider (e.g., corporate IdP)
 * 3. After successful authentication, SSO provider redirects back to this route
 * 4. URL contains: /auth/callback?code=xxx&state=yyy
 * 5. AWS Amplify automagically intercepts this URL and:
 *    - Exchanges the authorization code for OAuth tokens
 *    - Establishes the user session
 *    - Fires Hub events including 'customOAuthState' with our custom state that
 *      includes the redirect route
 *    - Cleans up the URL (removes query parameters)
 * 6. The Hub listener in useAuth.tsx catches 'customOAuthState' event and redirects
 *
 * WHY THIS COMPONENT IS EMPTY:
 * ------------------------------
 * - We CANNOT read state from URL params because Amplify removes them immediately
 * - All OAuth processing happens automatically via Amplify's Hub events
 * - The Hub listener in AuthProvider (useAuth.tsx) handles the redirect
 * - This component just serves as a landing page while processing occurs
 *
 * SAFETY MECHANISM:
 * -----------------
 * This component includes a 5-second timeout as a last-resort fallback.
 * If somehow the Hub event doesn't trigger a redirect (extremely unlikely),
 * we redirect to the login page to prevent users from being stuck.
 */
const AuthCallbackComponent = () => {
  const { logger } = useLogger();

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const timeoutError = new Error('OAuth callback timeout');
      logger.warn(
        'OAuth callback timeout triggered: Hub event did not redirect user within 5 seconds. Falling back to login page.',
        timeoutError,
        timeoutError.stack || '',
      );
      window.location.replace(`/${ROUTES.LOGIN}`);
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [logger]);

  return null;
};

export default AuthCallbackComponent;
